import { DndContext, closestCorners, type DragEndEvent } from '@dnd-kit/core'
import { useMemo } from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import { neighborsForMove } from '@/shared/lib/rank'

import { KanbanColumn } from './KanbanColumn'
import { createAnnouncements, createInstructions } from './announcements'
import { useDndSensors } from './sensors'
import type { KanbanColumnSpec, KanbanMovePayload, RankedItem, SortableRenderState } from './types'
import { useT } from '@/shared/i18n'

export interface KanbanBoardProps<T extends RankedItem> {
  columns: readonly KanbanColumnSpec<T>[]
  /** Un drop efectivo: reorden dentro de la columna o cambio de columna. */
  onMove: (payload: KanbanMovePayload) => void
  renderCard: (item: T, state: SortableRenderState) => ReactNode
  getLabel: (item: T) => string
  ariaLabel: string
  /** Se soltó sobre algo que no acepta el drop (la swimlane de permanentes). */
  onRejectedDrop?: (zoneId: string, reason?: string) => void
  /** Se dibuja DENTRO del contexto, arriba de las columnas: la swimlane fija. */
  header?: ReactNode
  className?: string
}

/**
 * Tablero kanban multi-columna.
 *
 * Decisión: la tarjeta **no cambia de columna mientras se arrastra**, recién al
 * soltar. Mover el item entre contenedores en cada `onDragOver` obliga a
 * mantener un estado espejo de la lista y es la fuente clásica del parpadeo
 * cuando llega la respuesta del server. Acá la columna destino se ilumina
 * mientras arrastrás y el movimiento lo hace el update optimista al soltar.
 */
export function KanbanBoard<T extends RankedItem>({
  columns,
  onMove,
  renderCard,
  getLabel,
  ariaLabel,
  onRejectedDrop,
  header,
  className,
}: KanbanBoardProps<T>) {
  const t = useT()
  const sensors = useDndSensors()
  const announcements = useMemo(() => createAnnouncements(t, 'board'), [t])
  const instructions = useMemo(() => createInstructions(t), [t])

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || over.id === active.id) return

    const from = columns.find((column) => column.items.some((item) => item.id === active.id))
    if (!from) return

    const overId = String(over.id)
    const to =
      columns.find((column) => column.id === overId) ??
      columns.find((column) => column.items.some((item) => item.id === overId))

    if (!to) {
      onRejectedDrop?.(overId)
      return
    }
    if ((to.accepts ?? true) === false) {
      onRejectedDrop?.(to.id, to.rejectReason)
      return
    }

    const fromIndex = from.items.findIndex((item) => item.id === active.id)

    if (to.id === from.id) {
      const newIndex = to.items.findIndex((item) => item.id === overId)
      if (newIndex === -1 || newIndex === fromIndex) return

      const { beforeId, afterId } = neighborsForMove(to.items, fromIndex, newIndex)

      onMove({
        itemId: String(active.id),
        fromIndex,
        newIndex,
        beforeId,
        afterId,
        fromColumnId: from.id,
        toColumnId: to.id,
        changedColumn: false,
      })
      return
    }

    // Otra columna: el item todavía no está en la lista destino, así que los
    // vecinos salen tal cual de `to.items`. Soltar sobre la columna (y no sobre
    // una tarjeta) manda al final. Si la columna está vacía viajan los dos en
    // `null` y el rank lo decide el server, que es el único que sabe dónde cae
    // esa columna dentro del orden global.
    const overIndex = to.items.findIndex((item) => item.id === overId)
    const newIndex = overIndex === -1 ? to.items.length : overIndex

    const before = to.items[newIndex - 1] ?? null
    const after = to.items[newIndex] ?? null

    onMove({
      itemId: String(active.id),
      fromIndex,
      newIndex,
      beforeId: before?.id ?? null,
      afterId: after?.id ?? null,
      fromColumnId: from.id,
      toColumnId: to.id,
      changedColumn: true,
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      accessibility={{ announcements, screenReaderInstructions: instructions }}
      onDragEnd={handleDragEnd}
    >
      {header}

      <div
        role="group"
        aria-label={ariaLabel}
        className={cn('scroll-x-visible flex gap-3 pb-2', className)}
      >
        {columns.map((column, index) => (
          <KanbanColumn
            key={column.id}
            column={column}
            columnIndex={index}
            renderCard={renderCard}
            getLabel={getLabel}
          />
        ))}
      </div>
    </DndContext>
  )
}
